/**
 * PersonalityTab - Configuración de personalidad del agente
 * Features: Identity, Tone Controls, System Prompt, Templates
 * Diseño: Light theme matching AdminCorrections (white cards, dark text, gradient bars)
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Chip,
  Button,
  Slider,
  Collapse,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Psychology as PsychologyIcon,
  LocalHospital as LocalHospitalIcon,
  Tune as TuneIcon,
  MedicalServices as MedicalIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from "@mui/icons-material";

// Tone presets (quick selection)
const TONE_PRESETS = [
  { id: "profesional", label: "Profesional" },
  { id: "amigable", label: "Amigable" },
  { id: "formal", label: "Formal" },
  { id: "casual", label: "Casual" },
  { id: "empatico", label: "Empático" },
];

// Tone slider configurations
const TONE_SLIDERS = [
  {
    id: "formality",
    label: "Formalidad",
    leftLabel: "Casual",
    rightLabel: "Formal",
    defaultValue: 70,
  },
  {
    id: "empathy",
    label: "Empatía",
    leftLabel: "Directo",
    rightLabel: "Empático",
    defaultValue: 80,
  },
  {
    id: "length",
    label: "Longitud",
    leftLabel: "Conciso",
    rightLabel: "Detallado",
    defaultValue: 50,
  },
  {
    id: "friendliness",
    label: "Cercanía",
    leftLabel: "Serio",
    rightLabel: "Amigable",
    defaultValue: 60,
  },
];

// Industry templates — Turismo
const PROMPT_TEMPLATES = {
  pasadias: {
    label: "Pasadías y Excursiones",
    icon: "🌴",
    prompt: `Eres el asistente virtual de un operador de pasadías y excursiones. Tu rol es:
- Informar sobre destinos, paquetes y precios
- Consultar disponibilidad de salidas por fecha
- Ayudar a crear reservaciones
- Ser entusiasta, amigable y conocedor del destino`,
  },
  hotel: {
    label: "Hotel / Hospedaje",
    icon: "🏨",
    prompt: `Eres el asistente virtual de un hotel. Tu rol es:
- Informar sobre habitaciones, tarifas y disponibilidad
- Ayudar a reservar habitaciones
- Responder sobre servicios, amenidades y ubicación
- Ser profesional y hospitalario`,
  },
  transporte: {
    label: "Transporte Turístico",
    icon: "🚐",
    prompt: `Eres el asistente virtual de una empresa de transporte turístico. Tu rol es:
- Informar sobre rutas, horarios y precios
- Ayudar a reservar cupos en transporte
- Dar indicaciones sobre puntos de recogida
- Ser puntual en la información y amable`,
  },
};

// Tipos de operación turística
const SPECIALTIES = [
  { id: "pasadias", label: "Pasadías y Excursiones" },
  { id: "transporte_nautico", label: "Transporte Náutico" },
  { id: "hotel", label: "Hotel / Hospedaje" },
  { id: "agencia_viajes", label: "Agencia de Viajes" },
  { id: "transporte_terrestre", label: "Transporte Terrestre" },
  { id: "restaurante", label: "Restaurante / Gastronomía" },
  { id: "buceo", label: "Buceo y Snorkel" },
  { id: "ecoturismo", label: "Ecoturismo" },
  { id: "cultural", label: "Turismo Cultural" },
  { id: "aventura", label: "Turismo de Aventura" },
  { id: "otro", label: "Otro tipo de operación" },
];

// Tone summary bar colors (multicolor per slider)
const TONE_BAR_COLORS = {
  formality: "#a855f7",
  empathy: "#06b6d4",
  length: "#f59e0b",
  friendliness: "#22c55e",
};

// ─── LIGHT THEME STYLES (matching AdminCorrections.js) ───

// Card style: white glassmorphism matching AdminCorrections StatCard pattern
const cardSx = {
  borderRadius: 3,
  background: "rgba(255, 255, 255, 0.85)",
  backdropFilter: "blur(20px)",
  boxShadow:
    "0 4px 24px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255,255,255,0.9)",
  border: "1px solid rgba(255, 255, 255, 0.5)",
  position: "relative",
  overflow: "hidden",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 8px 28px rgba(0, 0, 0, 0.12)",
  },
};

// Gradient bar pseudo-element helper (3px top bar)
const gradientBar = (from, to) => ({
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${from}, ${to})`,
  },
});

// Icon box helper (40x40 matching AdminCorrections)
const iconBox = (gradient, shadowColor) => ({
  width: 40,
  height: 40,
  borderRadius: 2,
  background: gradient,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  boxShadow: `0 4px 12px ${shadowColor}`,
  flexShrink: 0,
  "& svg": { fontSize: 20 },
});

// Light theme input styles
const inputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#fff",
    color: "#1e293b",
    fontSize: 13,
    borderRadius: 1.5,
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#06b6d4" },
  },
  "& .MuiInputLabel-root": {
    color: "#64748b",
    fontSize: 12,
    "&.Mui-focused": { color: "#06b6d4" },
  },
};

export default function PersonalityTab({
  config,
  chatbotName,
  onUpdate,
  onSave,
  saving,
  hasChanges,
}) {
  // Identity state
  const [identity, setIdentity] = useState({
    tipo_operacion: config?.tipo_operacion || "",
    nombre_operador: config?.nombre_operador || "",
    contacto_operador: config?.contacto_operador || "",
    tono_base: config?.tono_base || "",
    adaptar_al_turista: config?.adaptar_al_turista || false,
    deteccion_idioma: config?.deteccion_idioma !== false,
  });

  // Tone controls state
  const [toneValues, setToneValues] = useState({
    formality: config?.tone_formality || 70,
    empathy: config?.tone_empathy || 80,
    length: config?.tone_length || 50,
    friendliness: config?.tone_friendliness || 60,
  });

  const [selectedPreset, setSelectedPreset] = useState(null);
  const [maxTurns, setMaxTurns] = useState(config?.max_agent_turns || 5);
  const [expandedSections, setExpandedSections] = useState({
    identity: true,
    toneControls: true,
    templates: false,
  });

  // Update local state when config changes
  useEffect(() => {
    if (config) {
      setIdentity({
        tipo_operacion: config.tipo_operacion || "",
        nombre_operador: config.nombre_operador || "",
        contacto_operador: config.contacto_operador || "",
        tono_base: config.tono_base || "",
        adaptar_al_turista: config.adaptar_al_turista || false,
        deteccion_idioma: config.deteccion_idioma !== false,
      });
      setToneValues({
        formality: config.tone_formality || 70,
        empathy: config.tone_empathy || 80,
        length: config.tone_length || 50,
        friendliness: config.tone_friendliness || 60,
      });
      setMaxTurns(config.max_agent_turns || 5);
    }
  }, [config]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const [identityChanged, setIdentityChanged] = useState(false);

  const handleIdentityChange = (field, value) => {
    setIdentity((prev) => ({ ...prev, [field]: value }));
    setIdentityChanged(true);
  };

  const handleToneChange = (sliderId, value) => {
    setToneValues((prev) => ({ ...prev, [sliderId]: value }));
    setSelectedPreset(null); // Clear preset when manually adjusting
  };

  const applyPreset = (presetId) => {
    setSelectedPreset(presetId);
    const presets = {
      profesional: { formality: 85, empathy: 70, length: 60, friendliness: 50 },
      amigable: { formality: 40, empathy: 85, length: 50, friendliness: 90 },
      formal: { formality: 95, empathy: 60, length: 70, friendliness: 30 },
      casual: { formality: 25, empathy: 70, length: 40, friendliness: 85 },
      empatico: { formality: 60, empathy: 95, length: 65, friendliness: 75 },
    };
    if (presets[presetId]) {
      setToneValues(presets[presetId]);
    }
  };

  const applyTemplate = (templateKey) => {
    const template = PROMPT_TEMPLATES[templateKey];
    if (template) {
      onUpdate(template.prompt);
    }
  };

  const handlePromptChange = (e) => {
    onUpdate(e.target.value);
  };

  // Generate tone instructions based on slider values
  const generateToneInstructions = () => {
    const instructions = [];

    if (toneValues.formality > 70) {
      instructions.push("Usa un tono profesional y formal");
    } else if (toneValues.formality < 40) {
      instructions.push("Usa un tono casual y relajado");
    }

    if (toneValues.empathy > 70) {
      instructions.push("Muestra empatía y comprensión");
    }

    if (toneValues.length > 70) {
      instructions.push("Da respuestas detalladas y completas");
    } else if (toneValues.length < 40) {
      instructions.push("Sé conciso y directo");
    }

    if (toneValues.friendliness > 70) {
      instructions.push("Sé amigable y cercano");
    }

    return instructions.join(". ") + ".";
  };

  return (
    <Box sx={{ display: "flex", gap: 1.5, height: "100%" }}>
      {/* Left Column - Main Content */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Identity Section */}
        <Box sx={{ ...cardSx, ...gradientBar("#06b6d4", "#0891b2") }}>
          <Box
            onClick={() => toggleSection("identity")}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.25,
              px: 1.5,
              cursor: "pointer",
              "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={iconBox("linear-gradient(135deg, #06b6d4, #0891b2)", "rgba(6, 182, 212, 0.4)")}>
                <LocalHospitalIcon />
              </Box>
              <Box>
                <Typography sx={{ color: "#1e293b", fontSize: 14, fontWeight: 700 }}>
                  Tipo de Operación y Operador
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: 11 }}>
                  Información de tu consultorio
                </Typography>
              </Box>
            </Box>
            {expandedSections.identity ? (
              <CollapseIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
            ) : (
              <ExpandIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
            )}
          </Box>

          <Collapse in={expandedSections.identity} timeout={300}>
            <Box sx={{ p: 1.5, pt: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Specialty Dropdown */}
              <FormControl size="small" fullWidth>
                <InputLabel
                  sx={{
                    color: "#64748b",
                    fontSize: 12,
                    "&.Mui-focused": { color: "#06b6d4" },
                  }}
                >
                  Tipo de Operación
                </InputLabel>
                <Select
                  value={identity.tipo_operacion}
                  onChange={(e) => handleIdentityChange("tipo_operacion", e.target.value)}
                  label="Tipo de Operación"
                  sx={{
                    bgcolor: "#fff",
                    color: "#1e293b",
                    fontSize: 13,
                    borderRadius: 1.5,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#06b6d4" },
                    "& .MuiSvgIcon-root": { color: "#94a3b8" },
                  }}
                  startAdornment={<MedicalIcon sx={{ fontSize: 16, color: "#94a3b8", mr: 1 }} />}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: "#fff",
                        border: "1px solid #e2e8f0",
                        borderRadius: 2,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        maxHeight: 300,
                        "& .MuiMenuItem-root": {
                          color: "#1e293b",
                          fontSize: 13,
                          "&:hover": { bgcolor: "rgba(6, 182, 212, 0.08)" },
                          "&.Mui-selected": { bgcolor: "rgba(6, 182, 212, 0.12)" },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em style={{ color: "#94a3b8" }}>Seleccionar tipo de operación...</em>
                  </MenuItem>
                  {SPECIALTIES.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Clinic Name */}
              {/* Contacto del Operador */}
              <TextField
                size="small"
                label="Contacto del Operador"
                value={identity.contacto_operador}
                onChange={(e) => handleIdentityChange("contacto_operador", e.target.value)}
                placeholder="Ej: +57 305 260 8958, info@entretour.com"
                fullWidth
                multiline
                minRows={2}
                maxRows={3}
                sx={inputSx}
                InputProps={{
                  startAdornment: <LocationOnIcon sx={{ fontSize: 16, color: "#94a3b8", mr: 1, alignSelf: "flex-start", mt: 0.5 }} />,
                }}
              />

              {/* Tono, Acento e Idioma */}
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdfa', borderRadius: 2, border: '1px solid #ccfbf1' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#134e4a', mb: 1.5 }}>
                  🎭 Tono, Acento e Idioma
                </Typography>
                <FormControl fullWidth size="small" sx={inputSx}>
                  <InputLabel>Acento del chatbot</InputLabel>
                  <Select
                    value={identity.tono_base || 'neutro'}
                    label="Acento del chatbot"
                    onChange={(e) => handleIdentityChange("tono_base", e.target.value)}
                  >
                    <MenuItem value="neutro">🌎 Sin acento (español neutro)</MenuItem>
                    <MenuItem value="costeno">🏖️ Costeño caribeño — "mi llave", "bacano", "dale pues"</MenuItem>
                    <MenuItem value="rolo">🏙️ Rolo bogotano — "quiubo", "chévere", "sumercé"</MenuItem>
                    <MenuItem value="paisa">🏔️ Paisa antioqueño — "parce", "pues", "qué más"</MenuItem>
                    <MenuItem value="valluno">🌴 Valluno caleño — "vé", "mirá", tono alegre y musical</MenuItem>
                    <MenuItem value="santandereano">⛰️ Santandereano — directo, franco, "mijo"</MenuItem>
                    <MenuItem value="llanero">🐴 Llanero — "compadre", "vale", tono campechano</MenuItem>
                    <MenuItem value="pastuso">🌋 Pastuso — "achichay", tono tranquilo y amable</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Personalización adicional (opcional)"
                  value={identity.tono_extra || ''}
                  onChange={(e) => handleIdentityChange("tono_extra", e.target.value)}
                  placeholder="Ej: Menciona el mar, la brisa y el calor de Cartagena. Usa emojis de playa."
                  fullWidth
                  sx={{ ...inputSx, mt: 1.5 }}
                />
                <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={identity.adaptar_al_turista || false}
                        onChange={(e) => handleIdentityChange("adaptar_al_turista", e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography sx={{ fontSize: 12, color: '#475569' }}>Adaptar al turista</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={identity.deteccion_idioma !== false}
                        onChange={(e) => handleIdentityChange("deteccion_idioma", e.target.checked)}
                        size="small"
                        color="success"
                      />
                    }
                    label={<Typography sx={{ fontSize: 12, color: '#475569' }}>Detectar idioma</Typography>}
                  />
                </Box>
                <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.5 }}>
                  Adaptar: ajusta el acento al estilo del turista. Idioma: responde en inglés, portugués, francés, etc.
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </Box>

        {/* Tone Controls Section */}
        <Box sx={{ ...cardSx, ...gradientBar("#a855f7", "#6366f1") }}>
          <Box
            onClick={() => toggleSection("toneControls")}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.25,
              px: 1.5,
              cursor: "pointer",
              "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={iconBox("linear-gradient(135deg, #a855f7, #6366f1)", "rgba(168, 85, 247, 0.4)")}>
                <TuneIcon />
              </Box>
              <Box>
                <Typography sx={{ color: "#1e293b", fontSize: 14, fontWeight: 700 }}>
                  Control de Tono
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: 11 }}>
                  Ajusta la personalidad del asistente
                </Typography>
              </Box>
            </Box>
            {expandedSections.toneControls ? (
              <CollapseIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
            ) : (
              <ExpandIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
            )}
          </Box>

          <Collapse in={expandedSections.toneControls} timeout={300}>
            <Box sx={{ p: 1.5, pt: 0 }}>
              {/* Quick Presets */}
              <Box sx={{ display: "flex", gap: 0.75, mb: 1.5, flexWrap: "wrap" }}>
                {TONE_PRESETS.map((preset) => (
                  <Chip
                    key={preset.id}
                    label={preset.label}
                    size="small"
                    onClick={() => applyPreset(preset.id)}
                    sx={{
                      height: 26,
                      fontSize: 12,
                      fontWeight: 600,
                      bgcolor: selectedPreset === preset.id ? "#a855f7" : "transparent",
                      color: selectedPreset === preset.id ? "#fff" : "#7c3aed",
                      border: selectedPreset === preset.id
                        ? "1.5px solid #a855f7"
                        : "1.5px solid rgba(168, 85, 247, 0.3)",
                      boxShadow: selectedPreset === preset.id
                        ? "0 2px 8px rgba(168, 85, 247, 0.4)"
                        : "none",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: selectedPreset === preset.id ? "#9333ea" : "rgba(168, 85, 247, 0.08)",
                        transform: "scale(1.05)",
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Tone Sliders */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {TONE_SLIDERS.map((slider) => (
                  <Box key={slider.id}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography sx={{ color: "#475569", fontSize: 12, fontWeight: 600 }}>
                        {slider.label}
                      </Typography>
                      <Typography
                        sx={{
                          color: TONE_BAR_COLORS[slider.id],
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {toneValues[slider.id]}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ color: "#94a3b8", fontSize: 11, minWidth: 50 }}>
                        {slider.leftLabel}
                      </Typography>
                      <Slider
                        value={toneValues[slider.id]}
                        onChange={(e, val) => handleToneChange(slider.id, val)}
                        min={0}
                        max={100}
                        sx={{
                          color: TONE_BAR_COLORS[slider.id],
                          height: 6,
                          "& .MuiSlider-thumb": {
                            width: 16,
                            height: 16,
                            bgcolor: TONE_BAR_COLORS[slider.id],
                            border: "2px solid #fff",
                            boxShadow: `0 2px 6px ${TONE_BAR_COLORS[slider.id]}50`,
                            "&:hover": {
                              boxShadow: `0 0 0 6px ${TONE_BAR_COLORS[slider.id]}20`,
                            },
                          },
                          "& .MuiSlider-rail": { bgcolor: "#e2e8f0" },
                          "& .MuiSlider-track": {
                            border: "none",
                            borderRadius: 3,
                          },
                        }}
                      />
                      <Typography sx={{ color: "#94a3b8", fontSize: 11, minWidth: 50, textAlign: "right" }}>
                        {slider.rightLabel}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Generated Instructions Preview */}
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.25,
                  bgcolor: "rgba(168, 85, 247, 0.06)",
                  borderRadius: 2,
                  border: "1px solid rgba(168, 85, 247, 0.15)",
                }}
              >
                <Typography sx={{ color: "#64748b", fontSize: 11, fontWeight: 600, mb: 0.5, textTransform: "uppercase", letterSpacing: 0.3 }}>
                  Instrucciones generadas
                </Typography>
                <Typography sx={{ color: "#7c3aed", fontSize: 12, fontStyle: "italic" }}>
                  "{generateToneInstructions()}"
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </Box>

        {/* System Prompt */}
        <Box
          sx={{
            ...cardSx,
            ...gradientBar("#22c55e", "#16a34a"),
            flex: 1,
            p: 1.5,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Box sx={iconBox("linear-gradient(135deg, #22c55e, #16a34a)", "rgba(34, 197, 94, 0.4)")}>
              <PsychologyIcon />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: "#1e293b", fontSize: 14, fontWeight: 700 }}>
                System Prompt
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: 11 }}>
                Instrucciones principales para la IA
              </Typography>
            </Box>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={8}
            value={config?.system_prompt || ""}
            onChange={handlePromptChange}
            placeholder={`Eres el asistente virtual de ${chatbotName}...`}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#f8fafc",
                color: "#1e293b",
                fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
                fontSize: 13,
                height: "100%",
                alignItems: "flex-start",
                borderRadius: 2,
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: "#cbd5e1" },
                "&.Mui-focused fieldset": { borderColor: "#22c55e" },
              },
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
            <Button
              size="small"
              startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
              onClick={() => onUpdate("")}
              sx={{
                color: "#94a3b8",
                fontSize: 12,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              Limpiar
            </Button>
            {(hasChanges || identityChanged) && (
              <Button
                size="small"
                variant="contained"
                startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  onSave(identity);
                  setIdentityChanged(false);
                }}
                disabled={saving}
                sx={{
                  background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                  boxShadow: "0 4px 14px rgba(34, 197, 94, 0.4)",
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 2,
                  "&:hover": {
                    background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                    boxShadow: "0 6px 20px rgba(34, 197, 94, 0.5)",
                  },
                }}
              >
                {saving ? "Guardando..." : "Guardar Cambios"}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Right Column - Templates & Config */}
      <Box sx={{ width: 260, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Templates */}
        <Box sx={{ ...cardSx, ...gradientBar("#f59e0b", "#d97706") }}>
          <Box
            onClick={() => toggleSection("templates")}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.25,
              px: 1.5,
              cursor: "pointer",
              "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
            }}
          >
            <Typography sx={{ color: "#1e293b", fontSize: 13, fontWeight: 700 }}>
              Plantillas Rápidas
            </Typography>
            {expandedSections.templates ? (
              <CollapseIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
            ) : (
              <ExpandIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
            )}
          </Box>

          <Collapse in={expandedSections.templates} timeout={300}>
            <Box sx={{ p: 1.25, pt: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
              {Object.entries(PROMPT_TEMPLATES).map(([key, template]) => (
                <Button
                  key={key}
                  size="small"
                  onClick={() => applyTemplate(key)}
                  sx={{
                    justifyContent: "flex-start",
                    color: "#475569",
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: "none",
                    py: 0.75,
                    borderRadius: 1.5,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: "#f1f5f9",
                      borderColor: "#cbd5e1",
                      transform: "translateX(4px)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    },
                  }}
                >
                  {template.icon} {template.label}
                </Button>
              ))}
            </Box>
          </Collapse>
        </Box>

        {/* Advanced Config */}
        <Box sx={{ ...cardSx, ...gradientBar("#06b6d4", "#0891b2"), p: 1.5 }}>
          <Typography sx={{ color: "#1e293b", fontSize: 13, fontWeight: 700, mb: 1.5 }}>
            Configuración
          </Typography>

          <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 600, mb: 0.5 }}>
            Max. turnos antes de escalar
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Slider
              value={maxTurns}
              onChange={(e, val) => setMaxTurns(val)}
              min={1}
              max={15}
              sx={{
                color: "#06b6d4",
                height: 6,
                "& .MuiSlider-thumb": {
                  width: 14,
                  height: 14,
                  border: "2px solid #fff",
                  boxShadow: "0 2px 6px rgba(6,182,212,0.4)",
                },
                "& .MuiSlider-rail": { bgcolor: "#e2e8f0" },
                "& .MuiSlider-track": { border: "none" },
              }}
            />
            <Typography sx={{ color: "#0891b2", fontSize: 13, fontWeight: 700, minWidth: 20 }}>
              {maxTurns}
            </Typography>
          </Box>
          <Typography sx={{ color: "#94a3b8", fontSize: 11, mt: 0.5 }}>
            Intercambios antes de sugerir hablar con humano
          </Typography>
        </Box>

        {/* Tone Summary Card */}
        <Box
          sx={{
            ...cardSx,
            ...gradientBar("#8b5cf6", "#7c3aed"),
            p: 1.5,
          }}
        >
          <Typography sx={{ color: "#1e293b", fontSize: 12, fontWeight: 700, mb: 1, textTransform: "uppercase", letterSpacing: 0.3 }}>
            Perfil de Tono Actual
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {TONE_SLIDERS.map((slider) => (
              <Box key={slider.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ color: "#64748b", fontSize: 11, fontWeight: 600, minWidth: 60 }}>
                  {slider.label}
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    height: 6,
                    bgcolor: "#e2e8f0",
                    borderRadius: 3,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${toneValues[slider.id]}%`,
                      height: "100%",
                      bgcolor: TONE_BAR_COLORS[slider.id] || "#a855f7",
                      borderRadius: 3,
                      transition: "width 0.3s ease",
                    }}
                  />
                </Box>
                <Typography sx={{ color: TONE_BAR_COLORS[slider.id], fontSize: 11, fontWeight: 700, minWidth: 28, textAlign: "right" }}>
                  {toneValues[slider.id]}%
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
